import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { uploadImageToS3, UploadImageResponse } from '../services/s3UploadService';

interface S3UploadModalProps {
  open: boolean;
  onClose: () => void;
}

const S3UploadModal: React.FC<S3UploadModalProps> = ({ open, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploadedSizes, setUploadedSizes] = useState<{
    small?: string;
    medium?: string;
    large?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    setUploadedUrl(null);
    setUploadedSizes(null);
    setErrorMsg('');
    setSuccessMsg('');

    if (selected) {
      // Validar que sea una imagen
      if (!selected.type.startsWith('image/')) {
        setErrorMsg('El archivo debe ser una imagen');
        setFile(null);
        return;
      }

      // Crear preview local
      const localPreview = URL.createObjectURL(selected);
      setPreview(localPreview);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMsg('Primero selecciona una imagen');
      return;
    }

    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const response: UploadImageResponse = await uploadImageToS3(file);

      // contentUrl viene del backend y tiene el formato dev.petflix.io/content?key=...
      setUploadedUrl(response.contentUrl);
      
      // Guardar URLs de diferentes tamaños si están disponibles
      if (response.sizes) {
        setUploadedSizes({
          small: response.sizes.small?.contentUrl,
          medium: response.sizes.medium?.contentUrl,
          large: response.sizes.large?.contentUrl,
        });
      }
      
      setSuccessMsg('Imagen optimizada y subida exitosamente a S3');
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'Error al subir la imagen');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setUploadedUrl(null);
    setUploadedSizes(null);
    setErrorMsg('');
    setSuccessMsg('');
    onClose();
  };

  const handleCopyUrl = () => {
    if (uploadedUrl) {
      navigator.clipboard.writeText(uploadedUrl);
      setSuccessMsg('URL copiada al portapapeles');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Subir imagen a S3</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Input de archivo */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Seleccionar imagen
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ width: '100%' }}
            />
          </Box>

          {/* Preview local */}
          {preview && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Vista previa:
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={preview}
                  alt="preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: 8,
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Mensajes de error */}
          {errorMsg && (
            <Alert severity="error" onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          {/* Mensajes de éxito */}
          {successMsg && (
            <Alert severity="success" onClose={() => setSuccessMsg('')}>
              {successMsg}
            </Alert>
          )}

          {/* URL subida */}
          {uploadedUrl && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                URL para usar en la página (dev.petflix.io):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  value={uploadedUrl}
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyUrl}
                  size="small"
                >
                  Copiar
                </Button>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Vista desde el servidor (optimizada con lazy loading y srcset):
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {uploadedSizes?.small && uploadedSizes?.medium && uploadedSizes?.large ? (
                    <img
                      src={uploadedSizes.medium} // Fallback
                      srcSet={`
                        ${uploadedSizes.small} 400w,
                        ${uploadedSizes.medium} 800w,
                        ${uploadedSizes.large} 1600w
                      `}
                      sizes="(max-width: 600px) 400px,
                             (max-width: 1200px) 800px,
                             1600px"
                      loading="lazy"
                      alt="Imagen optimizada desde servidor"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: 8,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <img
                      src={uploadedUrl}
                      alt="desde servidor"
                      loading="lazy"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: 8,
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Box>
                {uploadedSizes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      URLs disponibles:
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, pl: 2, fontSize: '0.75rem' }}>
                      {uploadedSizes.small && (
                        <li>
                          <strong>Small (400px):</strong>{' '}
                          <code style={{ fontSize: '0.7rem' }}>{uploadedSizes.small}</code>
                        </li>
                      )}
                      {uploadedSizes.medium && (
                        <li>
                          <strong>Medium (800px):</strong>{' '}
                          <code style={{ fontSize: '0.7rem' }}>{uploadedSizes.medium}</code>
                        </li>
                      )}
                      {uploadedSizes.large && (
                        <li>
                          <strong>Large (1600px):</strong>{' '}
                          <code style={{ fontSize: '0.7rem' }}>{uploadedSizes.large}</code>
                        </li>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
        <Button
          onClick={handleUpload}
          disabled={loading || !file}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Subiendo...' : 'Subir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default S3UploadModal;
